// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Leaflet map: 112 stations as circle markers over faint real ABS state
// polygons. Markers coloured by the selected metric. Boundaries come from a real
// GeoJSON file — never hand-authored. The map frame is isolated (CSS) so
// Leaflet panes can't paint over drawers/modals.

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { ClimateData, Station } from '../types';
import { rateColor, anomalyColor } from '../color';
import { fmtRate, fmtSigned, esc } from '../format';
import { gloss } from '../glossary';
import { openDrawer } from '../drawer';
import { sectionHead } from './ui';

type Mode = 'rate' | 'recent' | 'nights' | 'hot';

interface ModeDef {
  label: string; legend: string;
  value: (s: Station) => number | null;
  color: (v: number | null) => string;
  fmt: (v: number | null) => string;
  ramp: Array<{ label: string; color: string }>;
}

export function mount(el: HTMLElement, data: ClimateData): () => void {
  let mode: Mode = (localStorage.getItem('warm.map.mode') as Mode) || 'rate';

  const modes: Record<Mode, ModeDef> = {
    rate: {
      label: 'Warming rate', legend: 'Full-record trend (°C/decade)',
      value: (s) => s.rateMean, color: (v) => rateColor(v, 0.3), fmt: (v) => fmtRate(v),
      ramp: [0, 0.075, 0.15, 0.225, 0.3].map((v) => ({ label: v.toFixed(2), color: rateColor(v, 0.3) })),
    },
    recent: {
      label: 'Recent warmth', legend: 'Last-10-year anomaly vs 1961–1990 (°C)',
      value: (s) => s.recentAnom, color: (v) => anomalyColor(v, 1.6), fmt: (v) => fmtSigned(v),
      ramp: [-1.6, -0.8, 0, 0.8, 1.6].map((v) => ({ label: (v > 0 ? '+' : '') + v.toFixed(1), color: anomalyColor(v, 1.6) })),
    },
    nights: {
      label: 'Nights vs days', legend: 'How much faster nights warm than days (°C/decade)',
      value: (s) => (s.rateMin != null && s.rateMax != null ? s.rateMin - s.rateMax : null),
      color: (v) => anomalyColor(v == null ? null : v * 6, 1.6), fmt: (v) => fmtSigned(v, 2),
      ramp: [-0.2, -0.1, 0, 0.1, 0.2].map((v) => ({ label: (v > 0 ? '+' : '') + v.toFixed(2), color: anomalyColor(v * 6, 1.6) })),
    },
    hot: {
      label: 'Hot-day trend', legend: 'Change in days ≥ 35 °C per decade',
      value: (s) => s.trendH35, color: (v) => anomalyColor(v == null ? null : v, 4), fmt: (v) => (v == null ? '—' : `${fmtSigned(v, 1, '')} days/decade`),
      ramp: [-4, -2, 0, 2, 4].map((v) => ({ label: (v > 0 ? '+' : '') + v, color: anomalyColor(v, 4) })),
    },
  };

  el.innerHTML = `
    <section class="panel">
      ${sectionHead('Where Australia is warming', `Every ${gloss('station', 'reference station')} on the map, coloured by the metric you choose. Hover a dot for its value; click to open the station\'s record. Faint state outlines are for context only.`)}
      <div class="controls" data-modes>
        ${(Object.keys(modes) as Mode[]).map((m) => `<button class="chip ${m === mode ? 'active' : ''}" data-mode="${m}">${modes[m].label}</button>`).join('')}
      </div>
      <div class="map-frame"><div class="map-canvas" data-map></div></div>
      <div class="map-legend" data-legend></div>
    </section>`;

  const canvas = el.querySelector('[data-map]') as HTMLElement;
  const legendEl = el.querySelector('[data-legend]') as HTMLElement;

  const map = L.map(canvas, { minZoom: 3, maxZoom: 9, zoomControl: true, scrollWheelZoom: false, attributionControl: true });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · Data © BOM ACORN-SAT (CC BY 4.0)', subdomains: 'abcd', minZoom: 3, maxZoom: 9,
  }).addTo(map);

  // Faint state outlines sit in their own pane BELOW the station markers.
  map.createPane('boundaries');
  map.getPane('boundaries')!.style.zIndex = '250';

  let markers: L.CircleMarker[] = [];

  const drawMarkers = () => {
    markers.forEach((m) => m.remove());
    markers = [];
    const def = modes[mode];
    for (const s of data.stations) {
      const v = def.value(s);
      const m = L.circleMarker([s.lat, s.lon], {
        radius: 6, weight: 1, color: '#334155', opacity: 0.7,
        fillColor: def.color(v), fillOpacity: 0.92,
      });
      m.bindTooltip(`${esc(s.name)} (${s.state}) — ${def.fmt(v)}`, { direction: 'top', opacity: 0.96, className: 'map-tip' });
      m.on('mouseover', () => m.setStyle({ weight: 2.5, radius: 8 }));
      m.on('mouseout', () => m.setStyle({ weight: 1, radius: 6 }));
      m.on('click', () => openDrawer(data, s.id));
      m.addTo(map);
      markers.push(m);
    }
    legendEl.innerHTML = `<span class="map-legend-title">${def.legend}</span><span class="ramp-key">${def.ramp.map((r) => `<span class="ramp-k"><i style="background:${r.color}"></i>${r.label}</span>`).join('')}</span>`;
  };

  // Markers first (independent of the boundaries fetch), then fit.
  drawMarkers();
  const fit = () => { map.invalidateSize(); map.fitBounds([[-43.8, 113], [-10.2, 154]], { padding: [10, 10] }); };
  const ro = new ResizeObserver(() => { if (canvas.clientHeight > 50) { fit(); ro.disconnect(); } });
  ro.observe(canvas);
  setTimeout(fit, 350);

  // Boundaries load into the lower pane so they never cover the markers.
  fetch('data/boundaries.geojson').then((r) => (r.ok ? r.json() : null)).then((geo) => {
    if (!geo) return;
    L.geoJSON(geo, {
      pane: 'boundaries', interactive: false,
      attribution: 'Boundaries: ABS ASGS (CC BY 4.0)',
      style: { fillColor: '#e9edf2', fillOpacity: 0.5, color: '#b9c2cd', weight: 0.8 },
    }).addTo(map);
  }).catch(() => { /* boundaries are optional context */ });

  el.querySelector('[data-modes]')?.addEventListener('click', (e) => {
    const b = (e.target as Element).closest('[data-mode]') as HTMLElement | null;
    if (!b) return;
    mode = b.getAttribute('data-mode') as Mode;
    localStorage.setItem('warm.map.mode', mode);
    el.querySelectorAll('[data-mode]').forEach((x) => x.classList.toggle('active', x === b));
    drawMarkers();
  });

  return () => { map.remove(); };
}
